import React, { SetStateAction, useEffect, useMemo, useState } from 'react';
import ImageCellRenderer from '@components/agile/agileComponents/agGridCommonComponents/ImageCellRenderer';
import { AgGridReact } from 'ag-grid-react';
import AgileUploadRow, { CapacityTuple } from '@components/agile/types/AgileUploadRow';
import BuyableCellRenderer from '@components/agile/agileComponents/AtAddNewAsin/AtAddTableGridComponent/BuyableCellRenderer';
import ProcurableCellRenderer from '@components/agile/agileComponents/AtAddNewAsin/AtAddTableGridComponent/ProcurableCellRenderer';
import TemperatureRatingCellRenderer from '@components/agile/agileComponents/AtAddNewAsin/AtAddTableGridComponent/TemperatureRatingCellRenderer';
import AtBulkUploadDeleteButtonCellRenderer from '@components/agile/agileComponents/AtAcrossZoneOperation/AtAcrossZoneComponents/AtAcrossZoneDiffTableComponent/AtDeleteButtonCellRenderer';
import { CellValueChangedEvent, ValueGetterParams } from 'ag-grid-community';

import useGetAssortmentDiff from '@store/hooks/useGetAssortmentDiff';
import { AgileMacroplanogram } from '@components/agile/types/AgileMacroplanogramTypes';
import { ProductEntry } from '@graphql/generated/graphql';
import AtZoneSelectionCellRenderer from '@components/agile/agileComponents/AtAcrossZoneOperation/AtAcrossZoneComponents/AtAcrossZoneDiffTableComponent/AtZoneSelectionCellRenderer';
import Loader from '@amzn/meridian/loader';
import Column from '@amzn/meridian/column';
import AtDiffSubCategoriesCellRenderer from '@components/agile/agileComponents/AtAcrossZoneOperation/AtAcrossZoneComponents/AtAcrossZoneDiffTableComponent/AtSubCategoriesCellRenderer';
import AtFohCapacityCellRenderer from '@components/agile/agileComponents/AtAcrossZoneOperation/AtAcrossZoneComponents/AtAcrossZoneDiffTableComponent/AtFohCapacityCellRenderer';
import Row from '@amzn/meridian/row';
import Button from '@amzn/meridian/button';
import Expander from '@amzn/meridian/expander';
import { AgileBatchRecommedationInput } from '@components/agile/types/AgileRecommendationTypes';
import AtRecommendedActionCellRenderer from '@components/agile/agileComponents/AtAcrossZoneOperation/AtAcrossZoneComponents/AtAcrossZoneDiffTableComponent/AtRecommendedActionCellRenderer';
import nonActionableColDefs from '@components/agile/utils/AcrossZoneDiffTableUtil';
import FacingQuantityButtonCellRenderer from '@components/agile/agileComponents/AtAcrossZoneOperation/AtAcrossZoneComponents/AtAcrossZoneDiffTableComponent/AtFacingQuantityButtonCellRenderer';
import {
  calculateTotalFacingUnitsPerZone,
  calculateTotalFacingUnitsPerZoneAndSubcategory,
  groupSelectionByMacroplanogramId,
} from '@components/agile/utils/AcrossZoneUtils';
import Modal from '@amzn/meridian/modal';
import Alert from '@amzn/meridian/alert';
import { createNoDiffItemsToast } from 'src/components/agile/utils/ToasterUtils';
import Toaster, { ToasterToasts } from '@amzn/meridian/toaster/toaster';
import ColumnSelector from '@components/shared/ColumnSelector';
import { useColumnSelector } from 'src/hooks/useColumnSelector';

export interface AtBulkUploadDiffTableProps {
  listOfMacroplanogram: AgileMacroplanogram[] | undefined;
  swapTime: number;
  inputAsins: string[];
  setRecommendationInput: (input: SetStateAction<AgileBatchRecommedationInput[]>) => void;
  setRecommendationFlag: (input: boolean) => void;
  setDiffTableData: (input: AgileUploadRow[]) => void;
  setMacroplanogramToFacingUnitsMap: (input: { [macroplanogramId: string]: number }) => void;
  setMacroplanogramToSubcategoryFacingUnitsMap: (input: {
    [macroplanogramId: string]: { [subcategoryId: string]: number };
  }) => void;
  setUpdatedRecommendInputWithDiffData: (value: AgileBatchRecommedationInput[]) => void;
}
const AtAcrossZoneDiffTable = (props: AtBulkUploadDiffTableProps) => {
  const [rowData, setRowData] = useState<AgileUploadRow[]>([]);
  const [nonActionableData, setNonActionableData] = useState<AgileUploadRow[]>([]);
  const [disableAdjustCapacityButton, setDisableAdjustCapacityButton] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [openError, setOpenError] = useState(true);
  const [showDiffTable, setShowDiffTable] = useState<boolean>(true);
  const [toasts, setToasts] = useState<ToasterToasts[]>([]);
  const openNoDiffItemsToaster = () => {
    setToasts(toasts.concat(createNoDiffItemsToast()));
  };
  const {
    listOfMacroplanogram,
    swapTime,
    inputAsins,
    setRecommendationInput,
    setRecommendationFlag,
    setDiffTableData,
    setMacroplanogramToFacingUnitsMap,
    setMacroplanogramToSubcategoryFacingUnitsMap,
    setUpdatedRecommendInputWithDiffData,
  } = props;
  const listOfMacroplanogramIds = listOfMacroplanogram?.map((obj) => obj.entryId);
  const productEntries: ProductEntry[] = inputAsins.map(
    (asin): ProductEntry => ({
      asin,
      subcategoryName: null,
    }),
  );
  const onCloseToast = (id: string) => setToasts(toasts.filter((t) => t.id !== id));

  const { result, isSuccess, isError } = useGetAssortmentDiff(
    {
      storeId: listOfMacroplanogram?.[0].storeId as string,
      timestamp: swapTime,
      macroplanogramIds: listOfMacroplanogramIds ?? [],
      productEntries,
      includeDraft: false,
    },
    listOfMacroplanogram,
  );

  useEffect(() => {
    setRowData(result.otherItems);
    setNonActionableData(result.nonActionableItems);
    const groupRowDataByMacroplanogram = groupSelectionByMacroplanogramId(
      result.otherItems,
      swapTime,
      listOfMacroplanogram,
    );
    // Calculate both zone-level and subcategory-level facing units
    const macroplanogramIdsToFacingsMap = calculateTotalFacingUnitsPerZone(groupRowDataByMacroplanogram);
    const macroplanogramToSubcategoryFacingsMap =
      calculateTotalFacingUnitsPerZoneAndSubcategory(groupRowDataByMacroplanogram);
    setMacroplanogramToFacingUnitsMap(macroplanogramIdsToFacingsMap);
    setMacroplanogramToSubcategoryFacingUnitsMap(macroplanogramToSubcategoryFacingsMap);
  }, [
    listOfMacroplanogram,
    result,
    setMacroplanogramToFacingUnitsMap,
    setMacroplanogramToSubcategoryFacingUnitsMap,
    swapTime,
  ]);

  useEffect(() => {
    if (isSuccess && result.otherItems.length === 0) {
      openNoDiffItemsToaster();
      setDisableAdjustCapacityButton(true);
      console.warn('The provided ASINs either already exist in the plan or are not actionable.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result.otherItems, isSuccess]);

  const colDefs = [
    {
      headerName: 'Action',
      field: 'action',
      cellRenderer: AtBulkUploadDeleteButtonCellRenderer,
      cellRendererParams: {
        rowData,
        setRowData,
        setDisableAdjustCapacityButton,
        setRecommendationFlag,
      },
      sortable: false,
      pinned: true,
      maxWidth: 100,
    },
    {
      headerName: 'Recommendation',
      field: 'action',
      maxWidth: 180,
      filter: true,
      cellRenderer: AtRecommendedActionCellRenderer,
      sortable: false,
      pinned: true,
    },
    {
      headerName: 'ASIN',
      field: 'asin',
      minWidth: 120,
      maxWidth: 150,
      filter: true,
      pinned: true,
    },
    {
      headerName: 'Title',
      field: 'title',
      filter: true,
      pinned: true,
    },

    {
      field: 'image',
      cellRenderer: ImageCellRenderer,
      sortable: false,
      pinned: true,
      maxWidth: 100,
    },
    {
      headerName: 'Facings',
      field: 'facingUnits',
      flex: 2,
      minWidth: 180,
      maxWidth: 180,
      cellRenderer: FacingQuantityButtonCellRenderer,
    },
    {
      headerName: 'Total FOH Capacity (Units)',
      field: 'fohCapacity',
      flex: 2,
      minWidth: 130,
      maxWidth: 165,
      headerClass: 'text-center',
      cellRenderer: AtFohCapacityCellRenderer,
      cellRendererParams: {
        rowData,
        setRowData,
        setDisableAdjustCapacityButton,
        listOfMacroplanogram,
      },
      comparator: (a: CapacityTuple, b: CapacityTuple) => Number(a.capacity) - Number(b.capacity),
    },
    {
      headerName: 'Zone',
      field: 'macroplanogramId',
      flex: 2,
      minWidth: 180,
      maxWidth: 180,
      cellRenderer: AtZoneSelectionCellRenderer,
      cellRendererParams: {
        rowData,
        setRowData,
        setRecommendationFlag,
        listOfMacroplanogram,
      },
      filter: 'agTextColumnFilter', // Enables text-based filtering
      valueGetter: (params: ValueGetterParams<AgileUploadRow>) => {
        const selectedMacropogId = params.data?.macroplanogramId;
        const selectedMacropog = listOfMacroplanogram?.find((macropog) => macropog.entryId === selectedMacropogId);
        return selectedMacropog ? selectedMacropog.macroplanogramName : '';
      },
    },
    {
      headerName: 'SubCategory',
      field: 'subcategoryId',
      editable: false,
      flex: 2,
      minWidth: 170,
      maxWidth: 170,
      cellRenderer: AtDiffSubCategoriesCellRenderer,
      cellRendererParams: {
        rowData,
        setRowData,
        listOfMacroplanogram,
      },
    },
    {
      headerName: 'Buyable',
      field: 'buyable',
      cellRenderer: BuyableCellRenderer,
    },
    {
      headerName: 'Procurable',
      field: 'procurable',
      cellRenderer: ProcurableCellRenderer,
    },
    {
      headerName: 'Temp Zone',
      field: 'temperatureRating',
      cellRenderer: TemperatureRatingCellRenderer,
    },
    {
      headerName: 'Shrink Cost (-1W)',
      field: 'shrinkCost',
      minWidth: 160,
      filter: true,
    },
    {
      headerName: 'Shrink % Ops (-1W) %',
      field: 'shrinkPerOps',
      filter: true,
      minWidth: 160,
      wrapHeaderText: true,
    },
    {
      headerName: 'Shrink % Ops (-13W) %',
      field: 'shrinkPerOps13week',
      filter: true,
      minWidth: 160,
      wrapHeaderText: true,
    },
    {
      headerName: 'Shrink % Ops (-26W) %',
      field: 'shrinkPerOps26week',
      filter: true,
      minWidth: 160,
      wrapHeaderText: true,
    },
    {
      headerName: 'Shrink % Ops (-52W) %',
      field: 'shrinkPerOps52week',
      filter: true,
      minWidth: 160,
      wrapHeaderText: true,
    },
    {
      headerName: 'Moving Avg Sales (-13W) $',
      field: 'movingAvgSales13week',
      filter: true,
      minWidth: 160,

      wrapHeaderText: true,
    },
    {
      headerName: 'Moving Avg Sales (-26W) $',
      field: 'movingAvgSales26week',
      filter: true,
      minWidth: 160,
      wrapHeaderText: true,
    },
    {
      headerName: 'Moving Avg Sales (-52W) $',
      field: 'movingAvgSales52week',
      filter: true,
      minWidth: 160,
      wrapHeaderText: true,
    },
    {
      headerName: 'UPHL1',
      field: 'uphl1',
      filter: true,
    },
    {
      headerName: 'UPHL2',
      field: 'uphl2',
      filter: true,
    },
    {
      headerName: 'UPHL3',
      field: 'uphl3',
      filter: true,
    },
  ];

  const defaultVisibleColumns = [
    'action',
    'asin',
    'title',
    'image',
    'facingUnits',
    'fohCapacity',
    'macroplanogramId',
    'subcategoryId',
    'buyable',
    'procurable',
    'temperatureRating',
    'shrinkPerOps',
    'shrinkPerOps13week',
    'movingAvgSales13week',
    'uphl1',
    'uphl2',
    'uphl3',
  ];

  const { visibleColumns, columnDefs, toggleColumn } = useColumnSelector({
    allColumns: colDefs,
    defaultVisibleColumns,
    storageKey: 'bulkOperationColumns',
  });

  const defaultColDef = useMemo(
    () => ({
      editable: false,
      sortable: true,
      flex: 2,
      resizable: true,
      filter: false,
      minWidth: 100,
      wrapHeaderText: true,
      autoHeaderHeight: true,
    }),
    [],
  );

  const onCellValueChangedHandler = (event: CellValueChangedEvent<AgileUploadRow>) => {
    if (event.rowIndex != null) {
      const newData = [...rowData];
      // eslint-disable-next-line no-param-reassign
      event.data.fohCapacity.capacity = (event.data.fohCapacityPerUnit as number) * (event.newValue as number);
      const index = newData.findIndex((item) => item.index === event.data.index);
      newData[index] = event.data;
      setRowData(newData);
      const groupRowDataByMacroPlanogram = groupSelectionByMacroplanogramId(newData, swapTime, listOfMacroplanogram);
      const macroplanogramIdsToFacingsMap = calculateTotalFacingUnitsPerZone(groupRowDataByMacroPlanogram);
      const macroplanogramToSubcategoryFacingsMap =
        calculateTotalFacingUnitsPerZoneAndSubcategory(groupRowDataByMacroPlanogram);
      setMacroplanogramToFacingUnitsMap(macroplanogramIdsToFacingsMap);
      setMacroplanogramToSubcategoryFacingUnitsMap(macroplanogramToSubcategoryFacingsMap);
      setUpdatedRecommendInputWithDiffData(groupRowDataByMacroPlanogram);
    }
  };
  const onClickHandler = () => {
    setRecommendationFlag(false);
    const updateRecommendation = groupSelectionByMacroplanogramId(rowData, swapTime, listOfMacroplanogram);
    const macroplanogramIdsToFacingsMap = calculateTotalFacingUnitsPerZone(updateRecommendation);
    const macroplanogramToSubcategoryFacingsMap = calculateTotalFacingUnitsPerZoneAndSubcategory(updateRecommendation);
    setMacroplanogramToFacingUnitsMap(macroplanogramIdsToFacingsMap);
    setMacroplanogramToSubcategoryFacingUnitsMap(macroplanogramToSubcategoryFacingsMap);
    setRecommendationInput(updateRecommendation);
    setUpdatedRecommendInputWithDiffData(updateRecommendation);
    // Set RecommendationFlag to true if
    // Adjust button is clicked again to re-render recommendation table.
    setTimeout(() => {
      setRecommendationFlag(true);
    }, 500);
    setDiffTableData(rowData);
  };

  return !isSuccess && !isError ? (
    <Column alignmentHorizontal="center" alignmentVertical="center" spacing="800" spacingInset="500">
      <Loader type="circular" size="large" />{' '}
    </Column>
  ) : (
    <>
      {isError && (
        <Modal
          open={openError}
          onClose={() => {
            setOpenError(false);
          }}
        >
          <Alert size="medium" type="error">
            <h3>Error Occurred</h3>
            <p>An error occurred while fetching the assortment diff data. Please check the input and try again.</p>
          </Alert>
        </Modal>
      )}
      {!isError && (
        <>
          <Toaster toasts={toasts} onCloseToast={onCloseToast}>
            {(toast) => <Alert toast {...toast} />}
          </Toaster>
          <Expander
            open={open}
            onChange={setOpen}
            title={`Non Actionable Asins: (${nonActionableData.length})`}
            type="list"
            spacingInset="500"
          >
            <div className="ag-theme-alpine">
              <AgGridReact<AgileUploadRow>
                rowData={nonActionableData}
                domLayout="autoHeight"
                columnDefs={nonActionableColDefs}
                defaultColDef={defaultColDef}
                rowSelection="multiple"
                enableCellTextSelection
                onCellValueChanged={onCellValueChangedHandler}
              />
            </div>
          </Expander>
          <Expander open={showDiffTable} onChange={setShowDiffTable} title="Diff Table" type="list">
            <Row alignmentHorizontal="end" spacingInset="400">
              <ColumnSelector allColumns={colDefs} visibleColumns={visibleColumns} onToggleColumn={toggleColumn} />
            </Row>
            <div className="ag-theme-alpine">
              <AgGridReact<AgileUploadRow>
                rowData={rowData}
                domLayout="autoHeight"
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                rowSelection="multiple"
                enableCellTextSelection
                onCellValueChanged={onCellValueChangedHandler}
              />
            </div>
          </Expander>

          <Row width="100%" alignmentHorizontal="center" spacingInset="300" backgroundColor="subdued">
            <Button onClick={onClickHandler} disabled={disableAdjustCapacityButton}>
              Adjust Existing Capacity
            </Button>
          </Row>
        </>
      )}
    </>
  );
};

export default AtAcrossZoneDiffTable;
